// Timeline Animation Configuration
interface TimelineConfig {
    animationSpeed: number;
    dotCount: number;
}

interface TimelineElement extends HTMLElement {
    getAttribute(name: 'fynd-timeline-element'): string | null;
    setAttribute(name: 'fynd-timeline-element', value: 'dot' | 'dot-filled' | 'dot-wrapper' | 'block' | 'circle' | 'circle-filled'): void;
}

interface TimelineBlock {
    blockElement: HTMLElement;
    circleElement: HTMLElement;
    dotWrapper: HTMLElement;
    blockIndex: number;
}

interface TimelineGroup {
    parentElement: HTMLElement;
    blocks: TimelineBlock[];
    currentBlockIndex: number;
    groupTimeout: number | null;
    containerTimeout: number | null;
}

// Configuration
let animationSpeed: number = 150;
let blockDelay: number = 0;
let fullCircleDelay: number = 1000;
let resetDelay: number = 1000;
let timelineGroups: Map<string, TimelineGroup> = new Map();

function createDots(): void {
    timelineGroups.clear();

    const groupElements = Array.from(document.querySelectorAll<HTMLElement>('[fynd-timeline]'));

    if (groupElements.length === 0) {
        return;
    }

    groupElements.forEach((groupElement: HTMLElement): void => {
        const groupId = groupElement.getAttribute('fynd-timeline');

        if (!groupId) {
            return;
        }

        const isVertical = groupElement.getAttribute('data-wf--timeline-block--variant') === 'vertical'
            || groupElement.closest('[data-wf--timeline-block--variant="vertical"]') !== null
            || groupElement.querySelector('[data-wf--timeline-block--variant="vertical"]') !== null;

        const dotCount: number = isVertical
            ? 10
            : window.innerWidth <= 991 ? 14 : 32;

        console.log('Timeline group:', groupId, '| isVertical:', isVertical, '| dotCount:', dotCount);

        const blockElements = Array.from(groupElement.querySelectorAll<HTMLElement>('[fynd-timeline-element="block"]'));

        if (blockElements.length === 0) {
            return;
        }

        const blocks: TimelineBlock[] = [];

        blockElements.forEach((blockElement: HTMLElement, index: number): void => {
            const circleElement = blockElement.querySelector<HTMLElement>('[fynd-timeline-element="circle"]');
            const dotWrapper = blockElement.querySelector<HTMLElement>('[fynd-timeline-element="dot-wrapper"]');

            if (!circleElement || !dotWrapper) {
                return;
            }

            dotWrapper.innerHTML = '';

            const isLastBlock = index === blockElements.length - 1;
            if (!isLastBlock) {
                for (let i: number = 0; i < dotCount; i++) {
                    const dot: HTMLElement = document.createElement('div');
                    dot.setAttribute('fynd-timeline-element', 'dot');
                    dot.dataset.index = i.toString();
                    dotWrapper.appendChild(dot);
                }
            }

            blocks.push({
                blockElement,
                circleElement,
                dotWrapper,
                blockIndex: index
            });
        });

        if (blocks.length > 0) {
            timelineGroups.set(groupId, {
                parentElement: groupElement,
                blocks: blocks,
                currentBlockIndex: 0,
                groupTimeout: null,
                containerTimeout: null
            });
        }
    });
}

function animateBlockElements(block: TimelineBlock, isLastBlock: boolean, onComplete: () => void): void {
    block.circleElement.setAttribute('fynd-timeline-element', 'circle-filled');

    if (isLastBlock) {
        setTimeout(onComplete, animationSpeed);
    } else {
        setTimeout(() => {
            animateContainerDots(block.dotWrapper, onComplete);
        }, animationSpeed);
    }
}

function animateContainerDots(container: HTMLElement, onComplete: () => void): number {
    const dots: NodeListOf<HTMLElement> = container.querySelectorAll<HTMLElement>('[fynd-timeline-element="dot"], [fynd-timeline-element="dot-filled"]');
    let currentIndex: number = 0;

    if (dots.length === 0) {
        setTimeout(onComplete, 500);
        return 0;
    }

    dots.forEach((dot: HTMLElement): void => dot.setAttribute('fynd-timeline-element', 'dot'));

    function fillNext(): void {
        if (currentIndex < dots.length) {
            dots[currentIndex].setAttribute('fynd-timeline-element', 'dot-filled');
            currentIndex++;
            setTimeout(fillNext, animationSpeed);
        } else {
            setTimeout(onComplete, 500);
        }
    }

    return window.setTimeout(fillNext, 0);
}

function resetGroupElements(groupId: string): void {
    const group = timelineGroups.get(groupId);
    if (!group) {
        return;
    }

    group.blocks.forEach((block: TimelineBlock): void => {
        block.circleElement.setAttribute('fynd-timeline-element', 'circle');

        const dots: NodeListOf<HTMLElement> = block.dotWrapper.querySelectorAll<HTMLElement>('[fynd-timeline-element="dot"], [fynd-timeline-element="dot-filled"]');
        dots.forEach((dot: HTMLElement): void => dot.setAttribute('fynd-timeline-element', 'dot'));
    });
}

function animateGroup(groupId: string): void {
    const group = timelineGroups.get(groupId);
    if (!group) {
        return;
    }

    stopGroupAnimation(groupId);

    function animateNextBlock(): void {
        if (!group) return;

        if (group.currentBlockIndex >= group.blocks.length) {
            group.groupTimeout = window.setTimeout(() => {
                resetGroupElements(groupId);
                group.currentBlockIndex = 0;
                group.groupTimeout = window.setTimeout(animateNextBlock, fullCircleDelay);
            }, resetDelay);
            return;
        }

        const currentBlock = group.blocks[group.currentBlockIndex];
        const isLastBlock = group.currentBlockIndex === group.blocks.length - 1;

        animateBlockElements(currentBlock, isLastBlock, (): void => {
            group.currentBlockIndex++;
            group.groupTimeout = window.setTimeout(animateNextBlock, blockDelay);
        });
    }

    animateNextBlock();
}

function stopGroupAnimation(groupId: string): void {
    const group = timelineGroups.get(groupId);
    if (!group) return;

    if (group.groupTimeout) {
        clearTimeout(group.groupTimeout);
        group.groupTimeout = null;
    }

    if (group.containerTimeout) {
        clearTimeout(group.containerTimeout);
        group.containerTimeout = null;
    }

    group.currentBlockIndex = 0;
}

function animateAllGroups(): void {
    if (timelineGroups.size === 0) {
        return;
    }

    timelineGroups.forEach((group: TimelineGroup, groupId: string): void => {
        animateGroup(groupId);
    });
}

function stopAllAnimations(): void {
    timelineGroups.forEach((group: TimelineGroup, groupId: string): void => {
        stopGroupAnimation(groupId);
    });
}

function setAnimationSpeed(newSpeed: number): void {
    if (newSpeed > 0) {
        animationSpeed = newSpeed;
    }
}

function setBlockDelay(newDelay: number): void {
    if (newDelay >= 0) {
        blockDelay = newDelay;
    }
}

function setFullCircleDelay(newDelay: number): void {
    if (newDelay >= 0) {
        fullCircleDelay = newDelay;
    }
}

function setResetDelay(newDelay: number): void {
    if (newDelay >= 0) {
        resetDelay = newDelay;
    }
}

function getTimelineConfig(): TimelineConfig {
    return {
        animationSpeed,
        dotCount: 5
    };
}

function getTimelineGroupsInfo(): { groupId: string; blockCount: number }[] {
    const info: { groupId: string; blockCount: number }[] = [];
    timelineGroups.forEach((group: TimelineGroup, groupId: string): void => {
        info.push({
            groupId,
            blockCount: group.blocks.length
        });
    });
    return info;
}

document.addEventListener('DOMContentLoaded', (): void => {
    createDots();
    setTimeout(animateAllGroups, 100);
});